import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Package,
  Users,
  Search,
  Loader2,
  AlertCircle,
  BookOpen,
  Sparkles,
  ArrowRight
} from 'lucide-react'
import { commerceApi, formatPrice, type ProductResponse } from '@/api/commerce'

export function ProductsPage() {
  const [searchKeyword, setSearchKeyword] = useState('')

  // 상품 목록 조회
  const { data: products = [], isLoading, error } = useQuery({
    queryKey: ['commerce', 'products'],
    queryFn: () => commerceApi.getProducts(),
    staleTime: 60 * 1000,
  })

  // 필터링된 상품 목록
  const filteredProducts = products.filter((product) => {
    // 활성화된 상품만
    if (product.status !== 'active') return false

    // 검색어 필터
    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase()
      if (
        !product.name.toLowerCase().includes(keyword) &&
        !product.shortDescription?.toLowerCase().includes(keyword)
      ) {
        return false
      }
    }

    return true
  })

  // 기본 가격 찾기
  const getDefaultPrice = (product: ProductResponse) => {
    const defaultPricing = product.pricingOptions.find((p) => p.isDefault)
    return defaultPricing || product.pricingOptions[0]
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="mb-4 w-12 h-12 animate-spin text-primary-600" />
        <p className="text-gray-600">상품 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <AlertCircle className="mb-4 w-16 h-16 text-red-400" />
        <p className="mb-2 font-medium text-gray-900">상품을 불러올 수 없습니다</p>
        <p className="text-gray-500">잠시 후 다시 시도해주세요</p>
      </div>
    )
  }

  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:py-12">
      {/* Hero Section */}
      <div className="mb-12 text-center">
        <div className="inline-flex gap-2 items-center px-4 py-2 mb-4 text-sm font-medium rounded-full bg-primary-50 text-primary-700">
          <Sparkles className="w-4 h-4" />
          깨봉 학습 상품
        </div>
        <h1 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl">
          나에게 맞는 학습을 시작하세요
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-gray-600">
          수학적 사고력과 문제 해결 능력을 키워주는 다양한 학습 상품을 만나보세요
        </p>
      </div>

      {/* Search */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
          <input
            type="text"
            placeholder="상품명으로 검색..."
            className="pl-12 w-full input"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="py-20 text-center bg-gray-50 rounded-2xl">
          <Package className="mx-auto mb-4 w-16 h-16 text-gray-300" />
          <p className="text-lg text-gray-500">
            {searchKeyword
              ? '조건에 맞는 상품이 없습니다'
              : '현재 판매 중인 상품이 없습니다'}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map((product) => {
            const pricing = getDefaultPrice(product)

            return (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="overflow-hidden bg-white rounded-2xl border border-gray-200 transition-all duration-300 group hover:shadow-xl hover:border-primary-300"
              >
                {/* Thumbnail */}
                <div className="overflow-hidden relative bg-linear-to-br aspect-video from-primary-100 to-primary-50">
                  {product.thumbnailUrl ? (
                    <img
                      src={product.thumbnailUrl}
                      alt={product.name}
                      className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex justify-center items-center h-full">
                      <BookOpen className="w-16 h-16 text-primary-300" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="mb-2 text-lg font-bold text-gray-900 transition-colors group-hover:text-primary-600 line-clamp-2">
                    {product.name}
                  </h3>

                  {product.shortDescription && (
                    <p className="mb-4 text-sm text-gray-600 line-clamp-2">
                      {product.shortDescription}
                    </p>
                  )}

                  {/* Meta Info */}
                  <div className="flex gap-4 items-center mb-4 text-sm text-gray-500">
                    {product.pricingOptions.length > 0 && (
                      <span className="flex gap-1 items-center">
                        <Users className="w-4 h-4" />
                        {product.pricingOptions.length}개 옵션
                      </span>
                    )}
                  </div>

                  {/* Connected Curriculums */}
                  {product.contents && product.contents.length > 0 && (
                    <div className="pt-3 mb-4 border-t border-gray-100">
                      <p className="mb-2 text-xs font-bold tracking-wider text-gray-400 uppercase">연결된 커리큘럼</p>
                      <div className="space-y-2">
                        {product.contents.map((content) => (
                          <div key={content.contentId} className="flex gap-2 justify-between items-center group/item">
                            <span className="text-sm text-gray-700 truncate flex-1 flex items-center gap-1.5">
                              <BookOpen className="w-3.5 h-3.5 text-primary-400" />
                              {content.title}
                            </span>
                            <Link
                              to={`/learn/curriculum/${content.contentId}`}
                              className="px-2 py-1 text-xs font-bold whitespace-nowrap rounded-md transition-colors text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100"
                              onClick={(e) => e.stopPropagation()}
                            >
                              학습 시작 →
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Price */}
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <div>
                      {pricing ? (
                        <>
                          {pricing.originalPrice && (
                            <span className="mr-2 text-sm text-gray-400 line-through">
                              {formatPrice(pricing.originalPrice, pricing.currency)}
                            </span>
                          )}
                          <span className="text-xl font-bold text-primary-600">
                            {formatPrice(pricing.price, pricing.currency)}
                          </span>
                          {pricing.discountPercentage && (
                            <span className="ml-2 text-sm font-medium text-red-500">
                              {pricing.discountPercentage}% 할인
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400">가격 문의</span>
                      )}
                    </div>
                    <span className="flex gap-1 items-center text-sm font-medium transition-all text-primary-600 group-hover:gap-2">
                      상세보기
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}

      {/* Total Count */}
      {filteredProducts.length > 0 && (
        <p className="mt-8 text-center text-gray-500">
          총 {filteredProducts.length}개의 상품
        </p>
      )}
    </div>
  )
}





















